import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { AppState } from '@app/reducers/interfaces';
import { getAuthenticatedUser } from '@app/reducers/orm/employee/employee.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { InfoCardComponent } from '@sb/ui/info-card';
import { clsx } from 'clsx';
import { environment } from 'src/environments/environment';

const academyUrlByLanguage = {
  nl: 'https://www.shiftbase.com/nl/academie',
  de: 'https://www.shiftbase.com/de/akademie',
};
type AcademyLanguage = keyof typeof academyUrlByLanguage;

const isAcademyLanguage = (language: string): language is AcademyLanguage =>
  Object.hasOwn(academyUrlByLanguage, language);

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'learn-tab',
  imports: [TranslatePipe, InfoCardComponent],
  templateUrl: './learn-tab.component.html',
  host: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '[class]': 'hostClasses',
  },
})
export class LearnTabComponent {
  #store = inject(Store<AppState>);

  hostClasses = clsx('flex flex-col gap-md');

  #authenticatedUser = this.#store.selectSignal(getAuthenticatedUser);
  #language = computed(() => this.#authenticatedUser()?.language ?? '');

  canAccessAcademy = computed(() => isAcademyLanguage(this.#language()));
  #academyUrl = computed(() => {
    const language = this.#language();

    return isAcademyLanguage(language) ? academyUrlByLanguage[language] : undefined;
  });

  openHelpCenter() {
    const supportedLanguages = ['nl', 'de', 'es']; // English is the default in the HC
    // KBLanguageCode doesn't include Spanish, but the HC does support it, so we need to map it manually
    let kbUrl = environment.knowledgeBaseUrl;
    if (supportedLanguages.includes(this.#language())) {
      kbUrl += `/${this.#language()}/`;
    }
    window.open(kbUrl, '_blank', 'noopener');
  }

  openAcademy() {
    const url = this.#academyUrl();
    if (url) {
      window.open(url, '_blank', 'noopener');
    }
  }
}
